// ABOUTME: Hook resolving the configurator drawer layout and rail behaviour from the cpq.configurator system settings.
// ABOUTME: Widths absent leave the drawer and package defaults; the expansion flags absent mean rail groups, recap features and the recap itself start collapsed.

import { SettingsContext } from '@src/modules/setting/SettingsContext';
import { useContext } from 'react';

/** Drawer width applied when the tenant configured none. */
export const DEFAULT_CONFIGURATOR_DRAWER_WIDTH = '85%';

export interface ConfiguratorLayout {
    /** Always set: the drawer needs a width, and there is no package default to fall back on. */
    drawerWidth: string;
    /** Undefined leaves the configurator's own rail width. */
    sidebarWidth: string | undefined;
    /** Undefined leaves the configurator's own recap width. */
    recapWidth: number | undefined;
    /**
     * Absent = groups collapsed: CoolPIM's default is the opposite of the package's, which opens
     * them so its other consumers keep the rail they have always had.
     */
    defaultGroupsExpanded: boolean;
    /** Absent = recap features collapsed, for the same reason as `defaultGroupsExpanded`. */
    defaultRecapFeaturesExpanded: boolean;
    /** Absent = recap column closed: open on load it takes a third of the drawer before anything is chosen. */
    defaultRecapExpanded: boolean;
}

/**
 * Reads settings.cpq directly (not via useCpqSettings) so the layout applies even when the CPQ
 * module is disabled, the same reason useConfiguratorCurrency gives.
 *
 * Blank strings are treated as unset, as usePimSidebarWidth does: a setting cleared in the JSON
 * editor by emptying it should read as "no override", not as a zero-width panel.
 */
export function useConfiguratorLayout(): ConfiguratorLayout {
    const settings = useContext(SettingsContext);
    const configurator = settings.cpq?.configurator;

    return {
        drawerWidth: configurator?.drawerWidth?.trim() || DEFAULT_CONFIGURATOR_DRAWER_WIDTH,
        sidebarWidth: configurator?.sidebarWidth?.trim() || undefined,
        recapWidth: configurator?.recapWidth ?? undefined,
        defaultGroupsExpanded: configurator?.defaultGroupsExpanded ?? false,
        defaultRecapFeaturesExpanded: configurator?.defaultRecapFeaturesExpanded ?? false,
        defaultRecapExpanded: configurator?.defaultRecapExpanded ?? false,
    };
}
